const API_BASE = process.env.REACT_APP_API_URL || '';

const STORAGE = {
  tenantId: 'flowfence_tenant_id',
  tenantName: 'flowfence_tenant_name',
  clientSecret: 'flowfence_client_secret',
  accessToken: 'flowfence_access_token',
  adminAccessToken: 'flowfence_admin_access_token',
  adminUsername: 'flowfence_admin_username',
};

export function getStoredAuth() {
  return {
    tenantId: localStorage.getItem(STORAGE.tenantId) || '',
    tenantName: localStorage.getItem(STORAGE.tenantName) || '',
    clientSecret: localStorage.getItem(STORAGE.clientSecret) || '',
    accessToken: localStorage.getItem(STORAGE.accessToken) || '',
  };
}

export function storeAuth({ tenantId, tenantName, clientSecret, accessToken }) {
  if (tenantId != null) localStorage.setItem(STORAGE.tenantId, tenantId);
  if (tenantName != null) localStorage.setItem(STORAGE.tenantName, tenantName);
  if (clientSecret != null) localStorage.setItem(STORAGE.clientSecret, clientSecret);
  if (accessToken != null) localStorage.setItem(STORAGE.accessToken, accessToken);
}

export function clearAuth() {
  localStorage.removeItem(STORAGE.tenantId);
  localStorage.removeItem(STORAGE.tenantName);
  localStorage.removeItem(STORAGE.clientSecret);
  localStorage.removeItem(STORAGE.accessToken);
}

export function getStoredAdminAuth() {
  return {
    accessToken: localStorage.getItem(STORAGE.adminAccessToken) || '',
    username: localStorage.getItem(STORAGE.adminUsername) || '',
  };
}

export function storeAdminAuth({ accessToken, username }) {
  if (accessToken != null) localStorage.setItem(STORAGE.adminAccessToken, accessToken);
  if (username != null) localStorage.setItem(STORAGE.adminUsername, username);
}

export function clearAdminAuth() {
  localStorage.removeItem(STORAGE.adminAccessToken);
  localStorage.removeItem(STORAGE.adminUsername);
}

export class ApiError extends Error {
  constructor(status, code, message, body = null) {
    super(message);
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

/** Called when an authenticated request gets 401 (expired/revoked JWT). */
let unauthorizedHandler = null;
let adminUnauthorizedHandler = null;

export function setUnauthorizedHandler(handler) {
  unauthorizedHandler = handler;
}

export function setAdminUnauthorizedHandler(handler) {
  adminUnauthorizedHandler = handler;
}

async function parseBody(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function handleUnauthorized(authMode) {
  if (authMode === 'admin') {
    clearAdminAuth();
    if (adminUnauthorizedHandler) {
      try {
        adminUnauthorizedHandler();
      } catch {
        // handler must not break error propagation
      }
    }
    return;
  }
  clearAuth();
  if (unauthorizedHandler) {
    try {
      unauthorizedHandler();
    } catch {
      // handler must not break error propagation
    }
  }
}

/**
 * @param {string} path
 * @param {{ method?: string, body?: unknown, auth?: boolean | 'tenant' | 'admin' }} options
 *   auth: true/'tenant' = tenant Bearer; 'admin' = admin Bearer; false = none
 */
export async function apiRequest(path, { method = 'GET', body, auth = true } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  const authMode = auth === true ? 'tenant' : auth;
  if (authMode === 'tenant') {
    const { accessToken } = getStoredAuth();
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  } else if (authMode === 'admin') {
    const { accessToken } = getStoredAdminAuth();
    if (!accessToken) {
      throw new ApiError(
        401,
        'missing_admin_token',
        'Not signed in as platform admin. Open /admin/login and sign in again.'
      );
    }
    headers.Authorization = `Bearer ${accessToken}`;
  }

  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body != null ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    throw new ApiError(0, 'network_error', err.message || 'Network request failed');
  }

  const data = await parseBody(response);

  if (!response.ok) {
    if (response.status === 401 && authMode) {
      handleUnauthorized(authMode);
    }
    const code = data && data.error ? data.error : 'request_failed';
    let message = data && data.message ? data.message : response.statusText;
    if (response.status === 404 && code === 'Not Found') {
      message = `API endpoint not found (${path}). Restart the backend so it includes the latest routes.`;
    }
    throw new ApiError(response.status, code, message, data);
  }

  return { data, status: response.status };
}

export const authApi = {
  register: (name) => apiRequest('/v1/tenants', { method: 'POST', body: { name }, auth: false }),
  login: (tenantId, clientSecret) =>
    apiRequest('/v1/auth/token', {
      method: 'POST',
      body: { tenantId, clientSecret },
      auth: false,
    }),
  getProfile: () => apiRequest('/v1/tenants/me'),
  updateProfile: (name) => apiRequest('/v1/tenants/me', { method: 'PATCH', body: { name } }),
  rotateSecret: (revokeExistingTokens = false) =>
    apiRequest('/v1/tenants/me/client-secret', {
      method: 'POST',
      body: { revokeExistingTokens },
    }),
  revokeSecret: () => apiRequest('/v1/tenants/me/client-secret', { method: 'DELETE' }),
};

export const adminApi = {
  login: (username, password) =>
    apiRequest('/v1/admin/auth/token', {
      method: 'POST',
      body: { username, password },
      auth: false,
    }),
  listTenants: () => apiRequest('/v1/admin/tenants', { auth: 'admin' }),
  getTenant: (id) => apiRequest(`/v1/admin/tenants/${id}`, { auth: 'admin' }),
  disableTenant: (id) =>
    apiRequest(`/v1/admin/tenants/${id}/disable`, { method: 'POST', auth: 'admin' }),
  enableTenant: (id) =>
    apiRequest(`/v1/admin/tenants/${id}/enable`, { method: 'POST', auth: 'admin' }),
  listOperators: () => apiRequest('/v1/admin/operators', { auth: 'admin' }),
  inviteOperator: (username) =>
    apiRequest('/v1/admin/operators/invites', {
      method: 'POST',
      body: { username },
      auth: 'admin',
    }),
  acceptInvite: (token, password) =>
    apiRequest('/v1/admin/invites/accept', {
      method: 'POST',
      body: { token, password },
      auth: false,
    }),
  rotatePassword: (currentPassword, newPassword) =>
    apiRequest('/v1/admin/operators/me/password', {
      method: 'POST',
      body: { currentPassword, newPassword },
      auth: 'admin',
    }),
  disableOperator: (id) =>
    apiRequest(`/v1/admin/operators/${id}/disable`, {
      method: 'POST',
      auth: 'admin',
    }),
};

export const rulesApi = {
  list: () => apiRequest('/v1/velocity/rules'),
  get: (id) => apiRequest(`/v1/velocity/rules/${id}`),
  create: (rule) => apiRequest('/v1/velocity/rules', { method: 'POST', body: rule }),
  update: (id, rule) => apiRequest(`/v1/velocity/rules/${id}`, { method: 'PUT', body: rule }),
  stats: (id) => apiRequest(`/v1/velocity/rules/${id}/stats`),
  evaluations: (id, limit = 50) =>
    apiRequest(`/v1/velocity/rules/${id}/evaluations?limit=${limit}`),
  disable: (id) => apiRequest(`/v1/velocity/rules/${id}/disable`, { method: 'POST' }),
  enable: (id) => apiRequest(`/v1/velocity/rules/${id}/enable`, { method: 'POST' }),
  delete: (id) => apiRequest(`/v1/velocity/rules/${id}`, { method: 'DELETE' }),
};

export const blocklistApi = {
  list: () => apiRequest('/v1/blocklist'),
  create: (entry) => apiRequest('/v1/blocklist', { method: 'POST', body: entry }),
  delete: (id) => apiRequest(`/v1/blocklist/${id}`, { method: 'DELETE' }),
};

export const decideApi = {
  evaluate: (payload) => apiRequest('/v1/decide', { method: 'POST', body: payload }),
};

export const tokensApi = {
  tokenize: (type, value) =>
    apiRequest('/v1/tokens', { method: 'POST', body: { type, value } }),
  detokenize: (token) =>
    apiRequest('/v1/tokens/detokenize', { method: 'POST', body: { token } }),
};
